import { createFileRoute } from "@tanstack/react-router";
import { useChat, type Message } from "@ai-sdk/react";
import {
	useEffect,
	useRef,
	type FormEvent,
	useState,
	useCallback,
	useMemo,
} from "react";
import { useStreamExample } from "@llm-ui/react";
import { ChatInput } from "~/components/practice/chat-input";
import { ChatThread } from "~/components/practice/chat-thread";
import { ChatHistorySidebar } from "~/components/practice/chat-history-sidebar";
import { ChatModeSwitcher } from "~/components/practice/chat-mode-switcher";
import {
	chatSessionsListQueryOptions,
	useCreateChatSession,
} from "~/lib/client/chat-sessions";
import type { ChatMode } from "~/lib/server/db/schema/chat-schema";
import { toast } from "sonner";
import { useChatMessages } from "~/lib/client/chat-sessions";
import { Button } from "~/components/ui/button";
import { HistoryIcon, LoaderCircleIcon, LoaderIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "~/components/ui/sheet";
import { cn } from "~/lib/utils";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { getLocale } from "~/i18n/runtime";

export const Route = createFileRoute("/app/chat")({
	component: ChatPage,
	loader: async ({ context }) => {
		await context.queryClient.prefetchQuery(
			chatSessionsListQueryOptions("regular"),
		);
	},
});

const DEMO_RESPONSES = [
	"日本のレストランでの注文ですね！いくつかポイントがありますよ。まず、席に案内されたら、店員さんが「ご注文はお決まりですか？」と聞いてくれるか、テーブルのベルを鳴らして呼びます。メニューを見ながら、食べたいものを選びます。もし日本語が難しければ、指差しでも大丈夫ですよ。注文する時は、*   「〜をください」 (〜 o kudasai) - 「〜をください」は「〜をください」という意味で、丁寧な言い方です。*   「〜をお願いします」 (〜 o onegai shimasu) - 「〜をお願いします」は「〜をお願いします」という意味で、これも丁寧な言い方です。例えば、ラーメンが食べたいなら「ラーメンをください」とか「ラーメンをお願いします」と言います。注文が終わったら、店員さんが復唱してくれることが多いので、合っているか確認してくださいね。⦅card,注文の仕方,レストランでの注文方法,1. 席に着く\n2. メニューを見る\n3. 店員を呼ぶ\n4. 「〜をください」または「〜をお願いします」で注文\n5. 注文内容を確認⦆⦅vocab,注文,ちゅうもん,order⦆⦅vocab,店員,てんいん,staff/waiter⦆⦅vocab,メニュー,めにゅー,menu⦆何か他に知りたいことはありますか？",
];

// used for testing stuff
const IS_DEMO = false;

interface ChatMainContentProps {
	chatId: string;
	mode: ChatMode;
	createChatMutation: ReturnType<typeof useCreateChatSession>;
	onSetChatId: (id: string) => void;
}

function ChatMainContent({
	chatId,
	mode,
	createChatMutation,
	onSetChatId,
}: ChatMainContentProps) {
	const [demoMessages, setDemoMessages] = useState<Message[]>([]);
	const [demoInput, setDemoInput] = useState("");
	const [currentDemoResponse] = useState(
		DEMO_RESPONSES[Math.floor(Math.random() * DEMO_RESPONSES.length)],
	);

	const { data: initialMessages } = useChatMessages(chatId);

	const {
		output: streamedOutput,
		isStreamFinished,
		reset: resetDemo,
	} = useStreamExample(currentDemoResponse, {
		autoStart: IS_DEMO,
		autoStartDelayMs: 1000,
		startIndex: 0,
		delayMultiplier: 1,
		tokenCharsProbabilities: [
			{ tokenChars: 2, prob: 0.5 },
			{ tokenChars: 4, prob: 0.5 },
		],
		delayMsProbabilities: [
			{ delayMs: 100, prob: 0.5 },
			{ delayMs: 20, prob: 0.5 },
		],
	});

	const pendingMessage = useRef<Message | null>(null);

	const {
		messages,
		handleInputChange,
		handleSubmit,
		status,
		input,
		append,
		setInput,
	} = useChat({
		api: "/api/chat",
		id: chatId,
		body: {
			id: chatId,
			mode,
			language: getLocale(),
		},
		initialMessages,
	});

	const handleDemoSubmit = useCallback(
		(e: FormEvent<Element>) => {
			e.preventDefault();
			if (!demoInput.trim()) return;

			const userMessage: Message = {
				id: `demo-user-${Date.now()}`,
				content: demoInput,
				role: "user",
			};

			setDemoMessages((prev) => [...prev, userMessage]);
			setDemoInput("");
			resetDemo();
		},
		[demoInput, resetDemo],
	);

	const streamingMessage = useMemo(() => {
		if (!IS_DEMO || !streamedOutput) return null;
		return {
			id: "demo-assistant-streaming",
			content: streamedOutput,
			role: "assistant" as const,
		};
	}, [streamedOutput]);

	const displayMessages = useMemo(() => {
		if (!IS_DEMO) return messages;
		const baseMessages = demoMessages;
		if (streamingMessage) {
			return [...baseMessages, streamingMessage];
		}
		return baseMessages;
	}, [messages, demoMessages, streamingMessage]);

	const demoStatus = useMemo(() => {
		if (!IS_DEMO) return "ready";
		if (!isStreamFinished && currentDemoResponse) return "streaming";
		return "ready";
	}, [isStreamFinished, currentDemoResponse]);

	const currentStatus = IS_DEMO
		? demoStatus
		: createChatMutation.isPending
			? "submitted"
			: status;
	const currentInput = IS_DEMO ? demoInput : input;

	const handleInputChangeWrapper = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (IS_DEMO) {
				setDemoInput(e.target.value);
			} else {
				handleInputChange(e);
			}
		},
		[handleInputChange],
	);

	const createNewChatSession = useCallback(
		async (messageContent: string) => {
			try {
				pendingMessage.current = {
					id: "pending_user_message",
					content: messageContent,
					role: "user",
				};

				const newSession = await createChatMutation.mutateAsync({
					initialMessageContent: messageContent,
					mode,
				});

				onSetChatId(newSession.id);
			} catch (error) {
				pendingMessage.current = null;
				toast.error("Failed to create new chat session.");
				console.error("Failed to create chat session:", error);
			}
		},
		[createChatMutation, onSetChatId, mode],
	);

	const handleRecommendationClick = useCallback(
		async (text: string) => {
			onSetChatId("");
			await createNewChatSession(text);
		},
		[createNewChatSession, onSetChatId],
	);

	useEffect(() => {
		if (
			pendingMessage.current &&
			chatId &&
			status === "ready" &&
			messages.length === 0
		) {
			append(pendingMessage.current);
			pendingMessage.current = null;
		}
	}, [chatId, status, append, messages.length]);

	const wrappedHandleSubmit = useCallback(
		async (e: FormEvent<Element>) => {
			if (IS_DEMO) {
				handleDemoSubmit(e);
				return;
			}

			e.preventDefault();
			if (!input.trim()) return;

			if (!chatId) {
				await createNewChatSession(input);
			} else {
				const formEvent = e as FormEvent<HTMLFormElement>;
				handleSubmit(formEvent);
			}

			setInput("");
		},
		[
			handleDemoSubmit,
			input,
			chatId,
			createNewChatSession,
			handleSubmit,
			setInput,
		],
	);

	return (
		<>
			<div className="flex-1 overflow-hidden">
				<ChatThread
					messages={displayMessages}
					status={currentStatus}
					onSelectRecommendation={handleRecommendationClick}
				/>
			</div>
			<ChatInput
				status={currentStatus}
				message={currentInput}
				onChange={handleInputChangeWrapper}
				onSubmit={wrappedHandleSubmit}
			/>
		</>
	);
}

function ChatPage() {
	const [chatId, setChatId] = useState("");
	const [mode, setMode] = useState<ChatMode>("regular");
	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

	const createChatMutation = useCreateChatSession();

	const handleMobileSidebarChatSelect = useCallback((newChatId: string) => {
		setChatId(newChatId ?? "");
		setIsMobileSidebarOpen(false);
	}, []);

	const handleModeChange = useCallback((newMode: ChatMode) => {
		setMode(newMode);
		setChatId("");
	}, []);

	return (
		<div
			className={cn(
				"w-full h-full flex relative bg-gradient-to-t from-pink-50/25 to-purple-50/25 lg:pr-72",
			)}
		>
			<div className="block md:hidden">
				<Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
					<SheetTrigger asChild>
						<Button
							variant="default"
							size="icon"
							className={cn(
								"fixed bg-white text-pink-500 border border-neutral-200 top-20 right-4 z-50 size-10 drop-shadow-lg",
							)}
						>
							<HistoryIcon className={cn("h-5 w-5")} />
						</Button>
					</SheetTrigger>
					<SheetContent
						side="right"
						className={cn("w-72 p-0")}
						withCloseButton={false}
					>
						<ChatHistorySidebar
							chatId={chatId}
							mode={mode}
							onSetSelectedChatId={handleMobileSidebarChatSelect}
						/>
					</SheetContent>
				</Sheet>
			</div>

			<div className="absolute top-0 left-0 p-4 backdrop-blur-sm">
				<ChatModeSwitcher currentMode={mode} onModeChange={handleModeChange} />
			</div>

			<div className="relative flex flex-col w-full max-w-screen-md mx-auto">
				<SuspenseFallback
					loading={
						<div className="w-full h-full flex justify-center items-center">
							<LoaderCircleIcon className="size-6 animate-spin text-pink-500/50" />
						</div>
					}
					error={
						<div className="p-4 text-red-500">Error loading chat messages</div>
					}
				>
					<ChatMainContent
						chatId={chatId}
						mode={mode}
						createChatMutation={createChatMutation}
						onSetChatId={setChatId}
					/>
				</SuspenseFallback>
			</div>

			<div className="w-72 hidden md:block fixed top-0 right-0 bottom-0">
				<ChatHistorySidebar
					chatId={chatId}
					mode={mode}
					onSetSelectedChatId={setChatId}
				/>
			</div>
		</div>
	);
}
